import React, { Fragment, useState, useMemo, useEffect } from "react";
import {
  Box,
  Typography,
  Snackbar,
  Alert,
  alpha,
  useTheme,
  Icon,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  Skeleton,
  Grid,
  Card,
  CardContent,
} from "@mui/material";
import { AccountFlowView } from "./components/AccountFlowView";
import { AccountTableView } from "./components/AccountTableView";
import { AccountDetailsDialog } from "./components/Dialogs/AccountDetailsDialog";
import { AccountFormDialog } from "./components/Dialogs/AccountFormDialog";
import { HelpDialog } from "./components/Dialogs/HelpDialog";
import { MergeAccountDialog } from "./components/Dialogs/MergeAccountDialog";
import { SplitAccountDialog } from "./components/Dialogs/SplitAccountDialog";
import { TransferBalanceDialog } from "./components/Dialogs/TransferBalanceDialog";
import { COAEmptyView } from "./components/COAEmptyView";

import { flattenAccounts } from "../../utils";
import { toast } from "react-toastify";
import { accountingService } from "../../../../services/accounting";
import {
  IListCoaRequest,
  ICoaAccount,
  ICreateCoaRequest,
  IUpdateCoaRequest,
  IDeleteCoaRequest,
  IMergeCoaRequest,
  ISplitCoaRequest,
  ITransferBalanceRequest,
  IChartOfAccount,
} from "../../accounting.interface";
import { useBranch } from "../../components/BranchContext";

// UI Account interface for component compatibility

const accountTypes = [
  {
    value: "Asset",
    label: "Assets",
    color: "#10B981",
    icon: "account_balance_wallet",
  },
  {
    value: "Liability",
    label: "Liabilities",
    color: "#EF4444",
    icon: "credit_card",
  },
  {
    value: "Equity",
    label: "Equity",
    color: "#3B82F6",
    icon: "trending_up",
  },
  {
    value: "Revenue",
    label: "Revenue",
    color: "#8B5CF6",
    icon: "monetization_on",
  },
  {
    value: "Expense",
    label: "Expenses",
    color: "#F59E0B",
    icon: "money_off",
  },
];

// Utility function to convert API response to UI format
const convertApiToUiFormat = (
  apiAccounts: ICoaAccount[]
): IChartOfAccount[] => {
  // Since live data account types are already in correct format, no mapping needed
  const convertedAccounts: IChartOfAccount[] = apiAccounts.map((account) => ({
    id: account.id,
    accountCode: account.account_code,
    accountName: account.account_name,
    accountType: account.account_type, // Direct assignment since format matches
    currency: account.currency,
    openingBalance: account.opening_balance,
    currentBalance: account.current_balance,
    parentAccountId: account.parent_account_id || undefined, // Convert null to undefined
    isParent: account.is_parent,
    allowDirectPosting: account.allow_direct_posting,
    level: account.level,
    isActive: account.is_active,
    createdAt: new Date(account.created_at).toISOString(),
    updatedAt: new Date(account.updated_at).toISOString(),
    children: [],
  }));

  return buildAccountHierarchy(convertedAccounts);
};

// Function to build hierarchical structure from flat array
const buildAccountHierarchy = (
  accounts: IChartOfAccount[]
): IChartOfAccount[] => {
  const accountMap = new Map<string, IChartOfAccount>();
  const rootAccounts: IChartOfAccount[] = [];

  // Create map for quick lookup
  accounts.forEach((account) => {
    accountMap.set(account.id, { ...account, children: [] });
  });

  // Build hierarchy
  accounts.forEach((account) => {
    const currentAccount = accountMap.get(account.id);
    if (!currentAccount) return;

    if (account.parentAccountId) {
      const parent = accountMap.get(account.parentAccountId);
      if (parent) {
        parent.children = parent.children || [];
        parent.children.push(currentAccount);
      }
    } else {
      rootAccounts.push(currentAccount);
    }
  });

  return rootAccounts;
};

// Loading Skeleton Components
const AccountTabsSkeleton: React.FC = () => (
  <Box sx={{ mb: 3 }}>
    <Skeleton variant="rectangular" height={80} sx={{ borderRadius: 1 }} />
  </Box>
);

const AccountGridSkeleton: React.FC = () => (
  <Grid container spacing={2}>
    {Array.from({ length: 6 }).map((_, index) => (
      <Grid item xs={12} sm={6} md={4} key={index}>
        <Card>
          <CardContent>
            <Skeleton variant="text" width="60%" height={24} />
            <Skeleton variant="text" width="40%" height={20} sx={{ mt: 1 }} />
            <Skeleton variant="rectangular" height={40} sx={{ mt: 2 }} />
          </CardContent>
        </Card>
      </Grid>
    ))}
  </Grid>
);

const AccountTableSkeleton: React.FC = () => (
  <Box sx={{ p: 2 }}>
    {Array.from({ length: 8 }).map((_, index) => (
      <Box
        key={index}
        sx={{ display: "flex", gap: 2, mb: 2, alignItems: "center" }}
      >
        <Skeleton variant="circular" width={24} height={24} />
        <Skeleton variant="text" width="30%" height={20} />
        <Skeleton variant="text" width="20%" height={20} />
        <Skeleton variant="text" width="25%" height={20} />
        <Skeleton variant="text" width="15%" height={20} />
      </Box>
    ))}
  </Box>
);

export const ChartOfAccountsPage: React.FC = () => {
  const theme = useTheme();
  const { selectedBranch } = useBranch();

  // State for COA context selection
  const [selectedEntity] = useState<string>("organization");
  const [viewMode, setViewMode] = useState<"chart" | "table">("chart");
  const [accounts, setAccounts] = useState<IChartOfAccount[]>([]);
  const [apiAccounts, setApiAccounts] = useState<ICoaAccount[]>([]);

  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [selectedAccount, setSelectedAccount] =
    useState<IChartOfAccount | null>(null);
  const [isDetailsDialogOpen, setIsDetailsDialogOpen] = useState(false);
  const [isFormDialogOpen, setIsFormDialogOpen] = useState(false);
  const [isHelpDialogOpen, setIsHelpDialogOpen] = useState(false);
  const [isMergeDialogOpen, setIsMergeDialogOpen] = useState(false);
  const [isSplitDialogOpen, setIsSplitDialogOpen] = useState(false);
  const [isTransferDialogOpen, setIsTransferDialogOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedLevel, setSelectedLevel] = useState<number | undefined>();
  const [selectedParentAccountId, setSelectedParentAccountId] = useState<
    string | undefined
  >();

  // Loading states
  const [coaLoading, setCOALoading] = useState(false);
  const [operationLoading, setOperationLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Flatten accounts for easier searching and validation
  const allAccounts = flattenAccounts(accounts);

  // Filter accounts by selected tab
  const filteredAccounts = useMemo(() => {
    const selectedType = accountTypes[selectedTab]?.value;
    if (!selectedType) return [];
    return accounts.filter((account) => account.accountType === selectedType);
  }, [accounts, selectedTab]);

  // Main data fetching function
  const fetchCOAData = async (showLoading = true) => {
    // Don't fetch if no branch is selected or organization level is selected
    if (!selectedBranch || selectedBranch === "organization") {
      setAccounts([]);
      setApiAccounts([]);
      setError(null);
      return;
    }

    if (showLoading) setCOALoading(true);
    setError(null);

    try {
      const requestData: IListCoaRequest = {
        pageIndex: 0,
        pageSize: 1000, // Get all accounts
        sortField: "account_code",
        sortOrder: "asc",
        selected_branch_id: selectedBranch,
      };

      const response = await accountingService.listCoa(requestData);

      setApiAccounts(response.data);
      const convertedAccounts = convertApiToUiFormat(response.data);
      setAccounts(convertedAccounts);
    } catch (error: any) {
      console.error("Failed to fetch COA data:", error);
      setError(error.message || "Failed to fetch COA data. Please try again.");
      toast.error("Failed to fetch COA data. Please try again.");
      setAccounts([]);
      setApiAccounts([]);
    } finally {
      if (showLoading) setCOALoading(false);
    }
  };

  // Initialize data on component mount and when branch changes
  useEffect(() => {
    fetchCOAData();
  }, [selectedBranch]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
    setSelectedAccount(null);
    setIsDetailsDialogOpen(false);
  };

  const handleViewModeChange = (
    event: React.MouseEvent<HTMLElement>,
    newViewMode: "chart" | "table" | null
  ) => {
    if (newViewMode !== null) {
      setViewMode(newViewMode);
    }
  };

  const handleNodeClick = (account: IChartOfAccount) => {
    setSelectedAccount(account);
    setIsDetailsDialogOpen(true);
  };

  const handleAddSubAccount = (parentAccount: IChartOfAccount) => {
    if (parentAccount.isActive === false) {
      toast.warning(
        "Cannot add sub-accounts to inactive accounts. Only active accounts can have sub-accounts added."
      );
      return;
    }
    setSelectedAccount(parentAccount);
    setSelectedLevel(parentAccount.level + 1);
    setSelectedParentAccountId(parentAccount.id);
    setIsEditMode(false);
    setIsFormDialogOpen(true);
  };

  const handleAddNewAtLevel = (level: number, parentAccountId?: string) => {
    setSelectedLevel(level);
    setSelectedParentAccountId(parentAccountId);
    setSelectedAccount(null);
    setIsEditMode(false);
    setIsFormDialogOpen(true);
  };

  const handleEditAccount = () => {
    if (selectedAccount?.isActive === false) {
      toast.warning(
        "Cannot edit inactive accounts. Only active accounts can be modified."
      );
      return;
    }
    if (selectedAccount) {
      setIsEditMode(true);
      setIsFormDialogOpen(true);
      setIsDetailsDialogOpen(false);
    }
  };

  const handleCloseDetailsDialog = () => {
    setIsDetailsDialogOpen(false);
    setSelectedAccount(null);
  };

  const handleCloseFormDialog = () => {
    setIsFormDialogOpen(false);
    setIsEditMode(false);
    setSelectedAccount(null);
    setSelectedLevel(undefined);
    setSelectedParentAccountId(undefined);
  };

  const handleOpenHelpDialog = () => {
    setIsHelpDialogOpen(true);
  };

  const handleCloseHelpDialog = () => {
    setIsHelpDialogOpen(false);
  };

  const handleSaveAccount = async (accountData: Partial<IChartOfAccount>) => {
    setOperationLoading(true);

    try {
      if (isEditMode && selectedAccount) {
        // Update existing account
        const updateData: IUpdateCoaRequest = {
          id: selectedAccount.id,
          account_name: accountData.accountName,
          parent_account_id: accountData.parentAccountId,
          is_active: accountData.isActive,
          allow_direct_posting: accountData.allowDirectPosting,
        };

        await accountingService.updateCoa(updateData);
        toast.success("Account updated successfully");
      } else {
        // Create new account
        const createData: ICreateCoaRequest = {
          account_name: accountData.accountName!,
          account_type: accountData.accountType as any,
          account_code: accountData.accountCode,
          parent_account_id:
            selectedParentAccountId || accountData.parentAccountId,
          opening_balance: accountData.openingBalance || 0,
          branch_id:
            selectedEntity === "organization" ? undefined : selectedBranch,
        };

        await accountingService.createCoa(createData);
        toast.success("Account created successfully");
      }

      // Refresh data
      await fetchCOAData(false);
      handleCloseFormDialog();
    } catch (error: any) {
      console.error("Failed to save account:", error);
      toast.error(error.message || "Failed to save account. Please try again.");
    } finally {
      setOperationLoading(false);
    }
  };

  const handleMergeAccount = (account: IChartOfAccount) => {
    if (account.isActive === false) {
      toast.warning(
        "Cannot merge inactive accounts. Only active accounts can be merged."
      );
      return;
    }
    setSelectedAccount(account);
    setIsMergeDialogOpen(true);
  };

  const handleSplitAccount = (account: IChartOfAccount) => {
    if (account.isActive === false) {
      toast.warning(
        "Cannot split inactive accounts. Only active accounts can be split."
      );
      return;
    }
    setSelectedAccount(account);
    setIsSplitDialogOpen(true);
  };

  const handleTransferBalance = (account: IChartOfAccount) => {
    if (account.isActive === false) {
      toast.warning(
        "Cannot transfer from inactive accounts. Only active accounts can transfer balance."
      );
      return;
    }
    if (!account.allowDirectPosting) {
      toast.warning(
        "Cannot transfer from parent accounts. Only leaf accounts can transfer balance."
      );
      return;
    }
    if (!account.currentBalance || account.currentBalance <= 0) {
      toast.warning(
        "Cannot transfer zero or negative balance. Account must have a positive current balance."
      );
      return;
    }
    setSelectedAccount(account);
    setIsTransferDialogOpen(true);
  };

  const handleMergeAccountExecute = async (mergeData: {
    sourceAccountId: string;
    targetAccountId: string;
    totalOpeningBalance: number;
    totalCurrentBalance: number;
  }) => {
    setOperationLoading(true);
    try {
      const requestData: IMergeCoaRequest = {
        source_account_id: mergeData.sourceAccountId,
        target_account_id: mergeData.targetAccountId,
      };

      await accountingService.mergeCoa(requestData);

      // Refresh data
      await fetchCOAData(false);
      setIsMergeDialogOpen(false);
      setSelectedAccount(null);
      toast.success("Accounts merged successfully");
    } catch (error: any) {
      console.error("Failed to merge accounts:", error);
      toast.error(
        error.message || "Failed to merge accounts. Please try again."
      );
    } finally {
      setOperationLoading(false);
    }
  };

  const handleSplitAccountExecute = async (splitData: {
    sourceAccountId: string;
    newAccounts: Array<{
      accountName: string;
      accountCode: string;
      openingBalance: number;
      currentBalance: number;
    }>;
  }) => {
    setOperationLoading(true);
    try {
      const requestData: ISplitCoaRequest = {
        original_account_id: splitData.sourceAccountId,
        auto_generate_codes: false,
        new_accounts: splitData.newAccounts.map((acc) => ({
          account_name: acc.accountName,
          account_code: acc.accountCode,
          opening_balance: acc.openingBalance,
          current_balance: acc.currentBalance,
        })),
      };

      await accountingService.splitCoa(requestData);

      // Refresh data
      await fetchCOAData(false);
      setIsSplitDialogOpen(false);
      setSelectedAccount(null);
      toast.success(
        `Account split into ${splitData.newAccounts.length} accounts successfully`
      );
    } catch (error: any) {
      console.error("Failed to split account:", error);
      toast.error(
        error.message || "Failed to split account. Please try again."
      );
    } finally {
      setOperationLoading(false);
    }
  };

  const handleTransferBalanceExecute = async (transferData: {
    sourceAccountId: string;
    targetAccountId: string;
    transferAmount: number;
  }) => {
    setOperationLoading(true);
    try {
      const requestData: ITransferBalanceRequest = {
        source_account_id: transferData.sourceAccountId,
        target_account_id: transferData.targetAccountId,
        transfer_amount: transferData.transferAmount,
        narration: `Balance transfer from account ${transferData.sourceAccountId} to ${transferData.targetAccountId}`,
      };

      await accountingService.transferBalance(requestData);

      // Refresh data
      await fetchCOAData(false);
      setIsTransferDialogOpen(false);
      setSelectedAccount(null);
      toast.success("Balance transferred successfully");
    } catch (error: any) {
      console.error("Failed to transfer balance:", error);
      toast.error(
        error.message || "Failed to transfer balance. Please try again."
      );
    } finally {
      setOperationLoading(false);
    }
  };

  // Handle creating first account
  const handleCreateFirstAccount = () => {
    setSelectedLevel(0);
    setSelectedParentAccountId(undefined);
    setSelectedAccount(null);
    setIsEditMode(false);
    setIsFormDialogOpen(true);
  };

  // Loading overlay for operations
  const renderLoadingOverlay = () => {
    if (operationLoading) {
      return (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: alpha(theme.palette.common.black, 0.5),
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <Box
            sx={{
              bgcolor: "background.paper",
              borderRadius: 2,
              p: 4,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
              minWidth: 200,
            }}
          >
            <CircularProgress size={40} />
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              Processing...
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", textAlign: "center" }}
            >
              Please wait while we process your request
            </Typography>
          </Box>
        </Box>
      );
    }
    return null;
  };

  return (
    <Fragment>
      {renderLoadingOverlay()}

      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        {/* Title Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
            p: 3,
            bgcolor: "background.paper",
            borderRadius: 0.5,
            border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
            boxShadow: `0 1px 3px ${alpha(theme.palette.common.black, 0.04)}`,
          }}
        >
          <Box>
            <Typography
              variant="h4"
              sx={{ fontWeight: 700, mb: 1, color: "text.primary" }}
            >
              Chart of Accounts
              {selectedBranch === "organization"
                ? " - Organization Level"
                : selectedBranch
                ? " - Branch Level"
                : ""}
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: "text.secondary", fontSize: "1rem" }}
            >
              {!selectedBranch
                ? "Select a branch to view and manage your financial accounts hierarchy"
                : "Visualize and manage your financial accounts hierarchy"}
              {coaLoading && (
                <Box
                  sx={{ display: "inline-flex", alignItems: "center", ml: 2 }}
                >
                  <CircularProgress size={16} sx={{ mr: 1 }} />
                  Loading accounts...
                </Box>
              )}
            </Typography>
          </Box>

          {/* Refresh Button */}
          <Tooltip title="Refresh accounts data">
            <IconButton
              onClick={() => fetchCOAData()}
              disabled={coaLoading}
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: "primary.main",
                "&:hover": {
                  bgcolor: alpha(theme.palette.primary.main, 0.2),
                },
              }}
            >
              <Icon>refresh</Icon>
            </IconButton>
          </Tooltip>
        </Box>

        {/* Account Type Tabs */}
        {selectedBranch && !error && accounts.length > 0 && !coaLoading ? (
          <Box
            sx={{
              mb: 3,
              bgcolor: "background.paper",
              borderRadius: 0.5,
              border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Tabs
              value={selectedTab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                flex: 1,
                "& .MuiTab-root": {
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.875rem",
                  minHeight: 64,
                  px: 3,
                  "&.Mui-selected": {
                    color: "primary.main",
                  },
                },
                "& .MuiTabs-indicator": {
                  height: 3,
                  borderRadius: "3px 3px 0 0",
                },
              }}
            >
              {accountTypes.map((type, index) => (
                <Tab
                  key={type.value}
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          bgcolor: type.color,
                        }}
                      />
                      <Icon sx={{ fontSize: 18 }}>{type.icon}</Icon>
                      {type.label}
                    </Box>
                  }
                />
              ))}
            </Tabs>

            {/* View Mode Toggle */}
            <Box sx={{ px: 3 }}>
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={handleViewModeChange}
                aria-label="view mode"
                size="small"
                sx={{
                  bgcolor: alpha(theme.palette.background.default, 0.5),
                  "& .MuiToggleButton-root": {
                    border: "none",
                    borderRadius: 1,
                    px: 2,
                    py: 1,
                    "&.Mui-selected": {
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      color: "primary.main",
                      "&:hover": {
                        bgcolor: alpha(theme.palette.primary.main, 0.15),
                      },
                    },
                    "&:hover": {
                      bgcolor: alpha(theme.palette.action.hover, 0.05),
                    },
                  },
                }}
              >
                <ToggleButton value="chart" aria-label="chart view">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Icon sx={{ fontSize: 18 }}>account_tree</Icon>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Chart
                    </Typography>
                  </Box>
                </ToggleButton>
                <ToggleButton value="table" aria-label="table view">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Icon sx={{ fontSize: 18 }}>table_rows</Icon>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Table
                    </Typography>
                  </Box>
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Box>
        ) : coaLoading ? (
          <AccountTabsSkeleton />
        ) : null}

        {/* Main Content */}
        <Box
          sx={{
            flex: 1,
            minHeight: "max-content",
            bgcolor: "background.paper",
            borderRadius: 0.5,
            border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
            p: viewMode === "table" ? 0 : 2,
            position: "relative",
          }}
        >
          {/* Conditional rendering based on state */}
          {!selectedBranch || selectedBranch === "organization" ? (
            // No branch selected or organization level selected
            <COAEmptyView type="no-branch" />
          ) : error ? (
            // Error state
            <COAEmptyView
              type="error"
              errorMessage={error}
              onRefresh={() => fetchCOAData()}
            />
          ) : coaLoading ? (
            // Loading state
            viewMode === "chart" ? (
              <AccountGridSkeleton />
            ) : (
              <AccountTableSkeleton />
            )
          ) : accounts.length === 0 ? (
            // No data state
            <COAEmptyView
              type="no-data"
              onCreateAccount={handleCreateFirstAccount}
              onRefresh={() => fetchCOAData()}
            />
          ) : // Normal view with data
          viewMode === "chart" ? (
            <>
              <AccountFlowView
                accounts={filteredAccounts}
                selectedAccount={selectedAccount || undefined}
                onNodeClick={handleNodeClick}
                onAddSubAccount={handleAddSubAccount}
                onAddNewAtLevel={handleAddNewAtLevel}
              />

              {/* Help Section - Top Left of Canvas */}
              <Box
                sx={{
                  position: "absolute",
                  top: 20,
                  left: 20,
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  bgcolor: alpha(theme.palette.background.paper, 0.95),
                  px: 3,
                  py: 1.5,
                  borderRadius: 0.5,
                  border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
                  backdropFilter: "blur(8px)",
                  boxShadow: `0 2px 8px ${alpha(
                    theme.palette.common.black,
                    0.1
                  )}`,
                  zIndex: 10,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    fontSize: "0.75rem",
                    fontWeight: 500,
                  }}
                >
                  💡 Switch between Organization & Branch COAs • Click nodes to
                  view details • Use + buttons to add sub-accounts
                </Typography>
                <Tooltip title="View Help" arrow>
                  <IconButton
                    size="small"
                    onClick={handleOpenHelpDialog}
                    sx={{
                      width: 28,
                      height: 28,
                      color: "info.main",
                      bgcolor: alpha(theme.palette.info.main, 0.1),
                      "&:hover": {
                        bgcolor: alpha(theme.palette.info.main, 0.2),
                      },
                    }}
                  >
                    <Icon sx={{ fontSize: 16 }}>help</Icon>
                  </IconButton>
                </Tooltip>
              </Box>
            </>
          ) : (
            <Box sx={{ p: 2 }}>
              <AccountTableView
                accounts={filteredAccounts}
                selectedAccount={selectedAccount || undefined}
                onNodeClick={handleNodeClick}
                onAddSubAccount={handleAddSubAccount}
              />
            </Box>
          )}
        </Box>
      </Box>

      {/* Account Details Dialog */}
      <AccountDetailsDialog
        open={isDetailsDialogOpen}
        onClose={handleCloseDetailsDialog}
        account={selectedAccount || undefined}
        onEdit={handleEditAccount}
        onAddSubAccount={() => {
          if (selectedAccount) {
            handleAddSubAccount(selectedAccount);
            setIsDetailsDialogOpen(false);
          }
        }}
        onMergeAccount={handleMergeAccount}
        onSplitAccount={handleSplitAccount}
        onTransferBalance={handleTransferBalance}
      />

      {/* Form Dialog */}
      <AccountFormDialog
        open={isFormDialogOpen}
        onClose={handleCloseFormDialog}
        account={selectedAccount ? selectedAccount : undefined}
        parentAccounts={allAccounts}
        onSave={handleSaveAccount}
        isLoading={operationLoading}
        isEditMode={isEditMode}
        selectedLevel={selectedLevel}
        selectedParentAccountId={selectedParentAccountId}
      />

      {/* Help Dialog */}
      <HelpDialog open={isHelpDialogOpen} onClose={handleCloseHelpDialog} />

      {/* Merge Account Dialog */}
      {selectedAccount && (
        <MergeAccountDialog
          open={isMergeDialogOpen}
          onClose={() => {
            setIsMergeDialogOpen(false);
            setSelectedAccount(null);
          }}
          sourceAccount={selectedAccount}
          availableAccounts={allAccounts}
          onMerge={handleMergeAccountExecute}
          isLoading={operationLoading}
        />
      )}

      {/* Split Account Dialog */}
      {selectedAccount && (
        <SplitAccountDialog
          open={isSplitDialogOpen}
          onClose={() => {
            setIsSplitDialogOpen(false);
            setSelectedAccount(null);
          }}
          sourceAccount={selectedAccount}
          onSplit={handleSplitAccountExecute}
          isLoading={operationLoading}
        />
      )}

      {/* Transfer Balance Dialog */}
      {selectedAccount && (
        <TransferBalanceDialog
          open={isTransferDialogOpen}
          onClose={() => {
            setIsTransferDialogOpen(false);
            setSelectedAccount(null);
          }}
          sourceAccount={selectedAccount}
          availableAccounts={allAccounts}
          onTransfer={handleTransferBalanceExecute}
          isLoading={operationLoading}
        />
      )}
    </Fragment>
  );
};
